import { Fragment, type ReactElement } from 'react'
import { Magnet, MessageSquare, MousePointer2, MoveUpRight, Trash2, Type } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ChannelIcon,
  FibonacciIcon,
  HorizontalLineIcon,
  LongPositionIcon,
  RectangleIcon,
  ShortPositionIcon,
  TrendlineIcon,
  VerticalLineIcon,
} from './WorkbenchDrawIcons'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../Tooltip/Tooltip'
import type { ToolDef } from './types'

// Workbench drawing rail (TradingView-style left rail). Buttons map to
// klinecharts overlay names; the parent calls createOverlay. "Cursor" clears the
// active tool. Drawing icons are TV-style hand-drawn glyphs (hollow-circle handles).
//
// The Buy/Sell entry tools capture a full setup in one guided motion — click the
// entry (why?), then a dotted line follows the cursor to the exit, click it (why?).
// Buy = long, Sell = short.

export const CURSOR_TOOL: ToolDef = { id: 'cursor', overlay: null, label: 'Cursor', Icon: MousePointer2 }

export const RAIL_TOOLS: ToolDef[] = [
  // Entry-setup tools first (2nd/3rd overall, right after the cursor) — the primary act here.
  { id: 'buy-entry', entrySetup: 'buy', label: 'Buy entry (mark your trade)', Icon: LongPositionIcon, iconClass: 'text-success', text: 'BUY', sep: true },
  { id: 'sell-entry', entrySetup: 'sell', label: 'Sell entry (mark your trade)', Icon: ShortPositionIcon, iconClass: 'text-danger', text: 'SELL' },
  // Notes group, right below the trade markers: free text + a callout whose tail pins
  // to a candle's O/H/L/C. Both type inline and travel to the AI.
  { id: 'text', note: 'text', click: true, label: 'Text', Icon: Type, sep: true },
  { id: 'callout', note: 'callout', click: true, label: 'Callout (note pinned to a candle)', Icon: MessageSquare },
  // Then the generic drawing tools, divided off by a separator. Arrow sits right below the trend line.
  { id: 'trend', overlay: 'segment', label: 'Trend line', Icon: TrendlineIcon, sep: true },
  { id: 'arrow', overlay: 'arrow', label: 'Arrow', Icon: MoveUpRight },
  { id: 'horizontal', overlay: 'horizontalStraightLine', label: 'Horizontal line', Icon: HorizontalLineIcon },
  { id: 'vertical', overlay: 'verticalStraightLine', label: 'Vertical line', Icon: VerticalLineIcon },
  { id: 'channel', overlay: 'parallelStraightLine', label: 'Parallel channel', Icon: ChannelIcon },
  { id: 'fib', overlay: 'fibRetracement', label: 'Fibonacci', Icon: FibonacciIcon },
  { id: 'rectangle', overlay: 'rectangle', label: 'Rectangle', Icon: RectangleIcon },
]

// A themed tooltip wrapper (replaces the slow, native `title` tooltips). Appears to
// the right of the rail, fast. Wraps any single trigger element via asChild.
function Hint({ label, children }: { label: string; children: ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  )
}

const toolBtn = (active: boolean) =>
  cn(
    'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors cursor-pointer',
    active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
  )

export interface WorkbenchDrawingRailProps {
  activeTool: string
  onSelectTool: (tool: ToolDef) => void
  onOpenDeleteMenu: (pos: { x: number; y: number }) => void
  magnet: boolean
  onToggleMagnet: () => void
}

export function WorkbenchDrawingRail({
  activeTool,
  onSelectTool,
  onOpenDeleteMenu,
  magnet,
  onToggleMagnet,
}: WorkbenchDrawingRailProps) {
  return (
    <TooltipProvider delayDuration={150}>
      <div className="shrink-0 w-11 flex flex-col items-center gap-0.5 py-2 border-r border-border bg-background">
        {/* Cursor */}
        <Hint label={CURSOR_TOOL.label}>
          <button
            type="button"
            aria-label={CURSOR_TOOL.label}
            aria-pressed={activeTool === CURSOR_TOOL.id}
            data-tool={CURSOR_TOOL.id}
            onClick={() => onSelectTool(CURSOR_TOOL)}
            className={toolBtn(activeTool === CURSOR_TOOL.id)}
          >
            <CURSOR_TOOL.Icon size={16} strokeWidth={1.75} />
          </button>
        </Hint>

        {/* Drawing tools (incl. the buy/sell entry setups — now the trade unit) */}
        {RAIL_TOOLS.map((t) => {
          const isActive = activeTool === t.id
          return (
            <Fragment key={t.id}>
              {t.sep && <div className="my-1 w-5 border-t border-border" />}
              <Hint label={t.label}>
                <button
                  type="button"
                  aria-label={t.label}
                  aria-pressed={isActive}
                  data-tool={t.id}
                  onClick={() => onSelectTool(t)}
                  className={toolBtn(isActive)}
                >
                  {t.text ? (
                    <span className={cn('text-[9px] font-semibold leading-none tracking-tight', t.iconClass)}>{t.text}</span>
                  ) : (
                    <t.Icon size={16} strokeWidth={1.75} className={cn(t.rotate && 'rotate-90', t.iconClass)} />
                  )}
                </button>
              </Hint>
            </Fragment>
          )
        })}

        <div className="mt-1 pt-1 border-t border-border w-7 flex flex-col items-center gap-0.5">
          {/* Magnet: snap drawing points to the nearest OHLC while drawing (toggle) */}
          <Hint label="Magnet (snap to OHLC while drawing)">
            <button
              type="button"
              aria-label="Magnet"
              aria-pressed={magnet}
              onClick={onToggleMagnet}
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors cursor-pointer',
                magnet ? 'bg-muted text-primary' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
              )}
            >
              <Magnet size={16} strokeWidth={1.75} />
            </button>
          </Hint>
          <Hint label="Remove drawings / indicators">
            <button
              type="button"
              aria-label="Remove"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect()
                onOpenDeleteMenu({ x: r.right + 6, y: r.top })
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors cursor-pointer"
            >
              <Trash2 size={16} strokeWidth={1.75} />
            </button>
          </Hint>
        </div>
      </div>
    </TooltipProvider>
  )
}
